import { useState } from 'react';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { Form, Input, Modal, Select, Button } from 'antd';
import { openNotification } from 'util/helpers';
import {
  useFindManyUserSceneAssetsQuery,
  useFindManyTourItemsQuery,
  useDeleteOneTourItemsMutation,
  useCreateOneTourItemsMutation,
  FindManyTourItemsDocument,
} from '@src/components/generated/graphql';
import { useSession } from 'next-auth/react';
import { DeleteOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import styles from './tour-config.module.css';
import DefaultButton from '@src/components/ui-kit/buttons/DefaultButton';

const notify = (type, msg, desc) => {
  openNotification(msg, desc, 'top', 2, type);
};

export default function TourConfig() {
  const [open, setOpen] = useState(false);
  const [createTourItem] = useCreateOneTourItemsMutation();
  const [deleteTourItem] = useDeleteOneTourItemsMutation();
  const { data: session } = useSession();
  const router = useRouter();
  const sceneId = Array.isArray(router.query?.id)
    ? router.query?.id[0]
    : router.query?.id;

  const { data: sceneAssets } = useFindManyUserSceneAssetsQuery({
    variables: {
      where: {
        userId: {
          equals: session?.user.id,
        },
        userSceneId: {
          equals: sceneId,
        },
        type: {
          in: [ASSET_TYPE.GLTF.OTHER, ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE],
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { data: tour, loading } = useFindManyTourItemsQuery({
    variables: {
      where: {
        userSceneId: {
          equals: sceneId,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const assets = sceneAssets?.findManyUserSceneAssets;
  const options = assets?.map((el) => {
    return { value: el.id, label: el.name };
  });

  const deleteItem = async (id: string) => {
    const res = await deleteTourItem({
      variables: {
        where: {
          id,
        },
      },
      refetchQueries: [FindManyTourItemsDocument],
    });

    if (res.errors || !res) {
      notify('error', 'Error', 'Something went wrong');
    } else {
      notify('success', 'Successfully', 'Deleted Item');
    }
  };

  const onSave = async (values) => {
    const position = assets?.find((el) => {
      return el.id === values.assetId;
    })?.position;

    if (!position) {
      notify('error', 'Error', 'Something went wrong');
      return;
    }

    const res = await createTourItem({
      variables: {
        data: {
          name: values.name,
          description: values.desc,
          position: {
            set: position!,
          },
          userScene: {
            connect: {
              id: sceneId,
            },
          },
        },
      },
      refetchQueries: [FindManyTourItemsDocument],
    });

    if (res.errors || !res) {
      notify('error', 'Error', 'Something went wrong');
    } else {
      notify('success', 'Successfully', 'saved changes');
      setOpen(false);
    }
  };

  if (loading) {
    return <>loading...</>;
  }

  return (
    <div>
      <h3 className={styles.title}>Tour Configuration</h3>
      <div className={styles.items}>
        {tour?.findManyTourItems.map((el, i) => {
          return (
            <div key={i} className={styles.cardContainer}>
              <div className={styles.card}>
                <span>{i + 1}. </span>
                <span>{el.name}</span>
              </div>
              <Button onClick={() => deleteItem(el.id)}>
                <DeleteOutlined />
              </Button>
            </div>
          );
        })}
        <DefaultButton onClick={() => setOpen(true)} style={{ marginTop: 20 }}>
          <span>+ Add Item</span>
        </DefaultButton>
      </div>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title='Create new Tour item'
        footer={false}
      >
        <Form layout='vertical' onFinish={onSave}>
          <Form.Item label='Name' required name='name'>
            <Input />
          </Form.Item>
          <Form.Item label='Description' required name='desc'>
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Asset's position" required name='assetId'>
            <Select options={options} />
          </Form.Item>
          <Button htmlType='submit' type='primary'>
            <span style={{ color: '#fff' }}>Create</span>
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
